import { createStyles } from '@/contexts/theme-context';
import { Dropdown, DropdownProps } from '@usepepper/ui/atoms/dropdown';
import { FlatList, TouchableOpacity, ViewStyle } from 'react-native';
import { ListItem, ListItemProps } from './list-item';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useHover } from 'react-native-web-hooks';
import { useRef } from 'react';

type DropdownMenuListItemProps = ListItemProps & {
  onPress: () => void;
};

const DropdownMenuListItem: React.FC<DropdownMenuListItemProps> = ({
  onPress,
  ...props
}) => {
  const [styles, theme] = useStyles();
  const touchableOpacityRef =
    useRef<React.ComponentRef<typeof TouchableOpacity>>(null);
  const isHovered = useHover(touchableOpacityRef);

  return (
    <TouchableOpacity
      onPress={onPress}
      ref={touchableOpacityRef}
      style={{
        backgroundColor: isHovered
          ? theme.sk.color.bg.primary.hover
          : theme.sk.color.bg.primary.default,
        ...styles.touchableOpacity,
      }}
    >
      <ListItem {...props} style={[styles.listItem, props.style]} />
    </TouchableOpacity>
  );
};

export interface DropdownMenuItemProps {
  key?: string;
  label: string;
  leftElement?: React.ReactNode;
  onPress: () => void;
  style?: ViewStyle;
}

export type DropdownMenuProps = Omit<DropdownProps, 'children'> & {
  listData: DropdownMenuItemProps[];
};

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  listData,
  ...dropdownProps
}) => {
  const [styles, theme] = useStyles();
  return (
    <Dropdown containerStyle={styles.dropdownBase} {...dropdownProps}>
      <FlatList
        contentContainerStyle={styles.flatListContainer}
        data={listData}
        keyExtractor={(item) => (item.key ? item.key : item.label)}
        renderItem={({ item }) => (
          <DropdownMenuListItem
            title={
              <Typography
                color={theme.sk.color.text.primary}
                numberOfLines={1}
                variant="paragraph-md"
              >
                {item.label}
              </Typography>
            }
            {...item}
          />
        )}
        showsVerticalScrollIndicator={false}
      />
    </Dropdown>
  );
};

const useStyles = createStyles((theme) => ({
  dropdownBase: {
    maxHeight: 270,
    paddingHorizontal: theme.sk.spacing.spaceXsmall,
  },
  flatListContainer: {
    padding: theme.sk.spacing.spaceXsmall,
  },
  listItem: {
    alignItems: 'center',
    minWidth: 124,
    padding: theme.sk.spacing.spaceSmall,
  },
  touchableOpacity: {
    borderRadius: theme.sk.radii.radiusSmall,
  },
}));
